import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children, cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { throttle, isFunction } from 'lodash';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { hasTouch } from 'Components/utils/feature-testing';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton';
import { themes } from './Slider.themes';
type Props = {
  children?: ReactNode;
  className?: string;
  cols?: number;
  hasControls?: boolean;
  hasTabindex?: boolean;
  id?: string;
  initialIndex?: number;
  navPosition?: 'bottom' | 'center' | 'centerOverlap' | 'top' | 'side';
  nbItemsToScroll?: number;
  nextLabel?: string;
  onChangeIndex?: (activeIndex: number) => void;
  onNext?: (e?: Event) => void;
  onPrevious?: (e?: Event) => void;
  previousLabel?: string;
  scrollAreaClassName?: string;
  scrollAreaContainerClassName?: string;
  scrollControlsClassName?: string;
  scrollItemClassName?: string | ((arg0: number) => string);
  scrollItemStyle?: React.CSSProperties | null | undefined;
  theme?: ThemeKey<typeof themes>;
};
export function Slider(props: Props) {
  const {
    className,
    children,
    cols = 1,
    hasControls,
    hasTabindex,
    id,
    initialIndex = 0,
    navPosition = 'top',
    nbItemsToScroll = 1,
    nextLabel,
    onChangeIndex,
    onNext,
    onPrevious,
    previousLabel,
    scrollAreaClassName,
    scrollAreaContainerClassName,
    scrollControlsClassName,
    scrollItemClassName,
    scrollItemStyle,
    theme = 'default',
  } = props;
  const childrenCount = Children.count(children);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const refsArray = useRef<Array<HTMLElement | null>>([]);
  const { isRTL } = useResponsive();
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [reachedX, setReachedX] = useState({
    hasReachedLeft: false,
    hasReachedRight: false,
  });
  const [hiddenIndexes, setHiddenIndexes] = useState<boolean[]>(
    Children.map(children, () => false) ?? [],
  );
  const [prevInitialIndex, setPrevInitialIndex] = useState<number | null>(null);
  useEffect(() => {
    if (onChangeIndex) {
      onChangeIndex(activeIndex);
    }
  }, [activeIndex]);

  const getHiddenIndexes = () => {
    if (cols > 0) {
      return;
    }

    if (containerRef.current) {
      const { clientWidth, scrollLeft, offsetLeft: containerOffsetLeft } = containerRef.current;
      const indexes = refsArray.current.filter(Boolean).map(({ offsetLeft, offsetWidth }) => {
        return (
          offsetLeft < scrollLeft + containerOffsetLeft ||
          offsetLeft + offsetWidth > scrollLeft + containerOffsetLeft + clientWidth
        );
      });
      setHiddenIndexes(indexes);
    }
  };

  const getReachedX = () => {
    if (containerRef.current) {
      const { clientWidth, scrollLeft, scrollWidth } = containerRef.current;
      const hasReachedLeft = scrollLeft === 0;
      const hasReachedRight = scrollLeft + clientWidth >= scrollWidth;
      setReachedX({
        hasReachedLeft,
        hasReachedRight,
      });
    }
  };

  const getSafeIndex = (idx: number) => {
    if (idx < 0) {
      return 0;
    }

    const maxIndex = cols > 0 ? childrenCount - cols : childrenCount - 1;
    return idx > maxIndex ? maxIndex : idx;
  };

  const scrollToIndex = (index: number, e?: React.SyntheticEvent | Event) => {
    if (e) {
      e.preventDefault();
    }

    const idx = getSafeIndex(index);
    const scrollValue = refsArray.current.slice(0, idx).reduce((v, ref) => {
      return v + (ref?.offsetWidth ?? 0);
    }, 0);

    if (containerRef.current?.scrollTo) {
      containerRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollValue,
      });
    }
  };

  useEffect(() => {
    getReachedX();
    getHiddenIndexes();
    scrollToIndex(initialIndex);
    setPrevInitialIndex(initialIndex);
  }, [children]);
  useEffect(() => {
    if (initialIndex !== prevInitialIndex) {
      scrollToIndex(initialIndex);
      setPrevInitialIndex(initialIndex);
    }
  }, [initialIndex]);

  if (!childrenCount) {
    return null;
  }

  const onScroll = throttle(() => {
    getReachedX();
    getHiddenIndexes();
    if (!containerRef.current) {
      return;
    }
    const { offsetLeft, scrollLeft } = containerRef.current;
    const positions = refsArray.current
      .filter((ref): ref is HTMLElement => Boolean(ref))
      .map((ref) => ref.offsetLeft);
    const closest = positions.reduce((prev, curr) => {
      return Math.abs(curr - scrollLeft) < Math.abs(prev - scrollLeft) ? curr : prev;
    }, offsetLeft);
    const pos = positions.indexOf(closest);

    if (activeIndex > pos) {
      onPrevious && onPrevious();
    } else if (activeIndex < pos) {
      onNext && onNext();
    }

    if (pos > -1) {
      setActiveIndex(pos);
      const el = refsArray.current[pos];
      const elFocused = hasTabindex ? el : (el?.firstChild as HTMLElement | null);
      elFocused?.focus({
        preventScroll: true,
      });
    }
  }, 250);
  const {
    thControl,
    thControlLeft,
    thControlRight,
    thControlColor,
    thControlDisabled,
    thControls,
    thInner,
    thRoot,
  } = themes[theme]({
    navPosition,
  }) as DefaultThemeStyles<typeof themes>;
  const { hasReachedLeft, hasReachedRight } = reachedX;
  const isSnapScrollDisabled = isRTL && hasTouch();
  return (
    <div
      className={classnames(
        thRoot,
        'relative',
        {
          '-scale-x-1 transform': isRTL,
        },
        className,
      )}
      dir="ltr"
      id={id}
    >
      <div className={classnames('h-full', thInner)}>
        <div className={scrollAreaClassName}>
          <div
            className={classnames('flex w-full flex-row flex-nowrap', scrollAreaContainerClassName)}
            onScroll={onScroll}
            ref={containerRef}
          >
            {Children.map(children, (child, idx) => {
              const isHidden =
                cols > 0 ? idx < activeIndex || idx >= activeIndex + cols : hiddenIndexes[idx];
              return (
                <div
                  aria-hidden={isHidden}
                  className={classnames(
                    isFunction(scrollItemClassName)
                      ? scrollItemClassName(idx)
                      : scrollItemClassName,
                    {
                      'snap-start': !isSnapScrollDisabled,
                      [`w-1/${cols}`]: cols > 1,
                      'w-full': cols === 1,
                      '-scale-x-1 transform': isRTL,
                    },
                  )}
                  key={idx}
                  ref={(ref) => {
                    refsArray.current[idx] = ref;
                  }}
                  {...(hasTabindex
                    ? {
                        tabIndex: -1,
                      }
                    : null)}
                  style={scrollItemStyle}
                >
                  {isValidElement(child)
                    ? cloneElement(child as React.ReactElement, {
                        dir: isRTL ? 'rtl' : 'ltr',
                      })
                    : child}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {hasControls && !(hasReachedLeft && hasReachedRight) && (
        <div className={classnames(scrollControlsClassName, thControls)}>
          <ArrowButton
            className={classnames(thControl, thControlLeft, {
              [thControlDisabled]: hasReachedLeft,
            })}
            color={thControlColor}
            hasNoRtl
            isDisabled={hasReachedLeft}
            label={previousLabel}
            onClick={(e) => scrollToIndex(activeIndex - nbItemsToScroll, e)}
          />
          <ArrowButton
            className={classnames(thControl, thControlRight, {
              [thControlDisabled]: hasReachedRight,
            })}
            color={thControlColor}
            hasNoRtl
            isDisabled={hasReachedRight}
            label={nextLabel}
            onClick={(e) => scrollToIndex(activeIndex + nbItemsToScroll, e)}
            rotation={180}
          />
        </div>
      )}
    </div>
  );
}
